import {
  buildAlerts, fmtCcy, startOfTodayMs, TARGET_CALLS
} from '../domain';
import type { Assignment, LogEntry, NewBizProspect, User } from '../types';
import { AlertBanner, AccountTypeBadge, PipelineBadge } from './Badges';
import { deadlineInfo } from '../domain';

interface Props {
  user: User;
  assignments: Assignment[];
  newbiz: NewBizProspect[];
  logs: LogEntry[];
  onOpenNewBiz: () => void;
  onSwitchToPipeline: () => void;
  onUpdate: (kind: 'assign' | 'newbiz', id: string) => void;
}

export function TodayTargets({ user, assignments, newbiz, logs, onOpenNewBiz, onSwitchToPipeline, onUpdate }: Props) {
  const now = Date.now();
  const todayTs = startOfTodayMs();

  const myAssigns = assignments.filter(a => a.amName === user.name);
  const myNb = newbiz.filter(n => n.amName === user.name);
  const all = [...myAssigns, ...myNb];

  const todayLogs = logs.filter(l =>
    l.ts >= todayTs && myAssigns.some(a => a.custId === l.customerId)
  );

  const totalAssigned = all.length;
  const called = all.filter(a => ['called', 'quoted', 'won', 'lost'].includes(a.status)).length;
  const researched = all.filter(a => ['researched', 'called', 'quoted', 'won', 'lost'].includes(a.status)).length;
  const followups = all.filter(a => a.status === 'followup').length;
  const overdue = all.filter(a => a.deadline < now && a.status !== 'won' && a.status !== 'lost').length;
  const todayCalls = todayLogs.filter(l => l.type === 'call').length;
  const remaining = Math.max(0, TARGET_CALLS - todayCalls);
  const rev = all.filter(a => a.status === 'won').reduce((s, a) => s + (a.revenue || 0), 0);
  const converted = all.filter(a => a.status === 'won').length;

  const alerts = buildAlerts(user.name, assignments, newbiz, logs);

  const sortedAll = [
    ...myAssigns.map(a => ({ ...a, _kind: 'assign' as const, _displayName: a.custName })),
    ...myNb.map(n => ({ ...n, _kind: 'newbiz' as const, _displayName: n.companyName }))
  ].sort((a, b) => b.assignedDate - a.assignedDate);

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        {alerts.map((a, i) => <AlertBanner key={i} type={a.type} msg={a.msg} />)}
      </div>

      <div className="section-hdr">
        <div className="section-hdr-title">Today's <span style={{ color: 'var(--accent)', fontStyle: 'italic' }}>Targets</span></div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="action-btn btn-note" onClick={onOpenNewBiz}>+ New Prospect</button>
          <button className="action-btn btn-email" onClick={onSwitchToPipeline}>📋 My Pipeline</button>
        </div>
      </div>

      <div className="targets-grid">
        <div className="target-card">
          <div className="target-card-val" style={{ color: 'var(--accent2)' }}>{totalAssigned}</div>
          <div className="target-card-lbl">Total Assigned</div>
        </div>
        <div className={`target-card ${todayCalls >= TARGET_CALLS ? 'complete' : ''}`}>
          <div className="target-card-val" style={{ color: 'var(--call)' }}>{todayCalls}</div>
          <div className="target-card-lbl">Calls Today</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>target: {TARGET_CALLS}</div>
        </div>
        <div className={`target-card ${remaining > 0 ? 'urgent' : 'complete'}`}>
          <div className="target-card-val" style={{ color: remaining > 0 ? 'var(--danger)' : 'var(--call)' }}>{remaining}</div>
          <div className="target-card-lbl">Calls Remaining</div>
        </div>
        <div className="target-card">
          <div className="target-card-val" style={{ color: 'var(--note)' }}>{researched}</div>
          <div className="target-card-lbl">Researched</div>
        </div>
        <div className={`target-card ${followups > 0 ? 'urgent' : ''}`}>
          <div className="target-card-val" style={{ color: 'var(--note)' }}>{followups}</div>
          <div className="target-card-lbl">Follow-ups Due</div>
        </div>
        <div className={`target-card ${overdue > 0 ? 'urgent' : ''}`}>
          <div className="target-card-val" style={{ color: overdue > 0 ? 'var(--danger)' : 'var(--text-dim)' }}>{overdue}</div>
          <div className="target-card-lbl">Overdue</div>
        </div>
        <div className="target-card">
          <div className="target-card-val" style={{ color: 'var(--call)', fontSize: rev > 0 ? 18 : 28 }}>
            {rev > 0 ? fmtCcy(rev) : '0'}
          </div>
          <div className="target-card-lbl">Revenue Won</div>
        </div>
        <div className="target-card">
          <div className="target-card-val" style={{ color: 'var(--call)' }}>{converted}</div>
          <div className="target-card-lbl">Converted</div>
        </div>
      </div>

      {/* Recent assignments */}
      <div className="mgmt-section" style={{ marginTop: 18 }}>
        <div className="section-hdr">
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 17 }}>My Assignments</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {myAssigns.length} accounts · {myNb.length} prospects · {called}/{totalAssigned} contacted
          </div>
        </div>
        {sortedAll.length === 0 ? (
          <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>No assignments yet. Ask your manager to assign accounts.</p>
        ) : (
          sortedAll.map(a => {
            const dl = deadlineInfo(a.deadline);
            return (
              <div key={a.id} className="assign-row">
                <div className="assign-name">{a._displayName || '—'}</div>
                <AccountTypeBadge type={a._kind === 'newbiz' ? 'newbiz' : (a as Assignment).accountType} />
                <PipelineBadge status={a.status} />
                <div className={dl.cls} style={{ fontSize: 10 }}>{dl.label}</div>
                <button className="entry-del-btn" onClick={() => onUpdate(a._kind, a.id)}>Update</button>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
